// src/pages/userCreate.js
import React, { useEffect, useState } from 'react';
import { Container, Form, Button, Card, Accordion } from 'react-bootstrap';
import { useParams, useNavigate } from 'react-router-dom';
import { fetchUserById, createUser, fetchPermissions, fetchUsers } from '../redux/actions/userActions';
import { useDispatch, useSelector } from 'react-redux';
import CreateUserRoleSelect from './components/users/CreateUserRoleSelect';
import PermissionsForm from './components/users/CreateUserPermissions';
import GlobalModal from '../pages/components/modals/globalModal';

function UserCreate() {
  const { userId } = useParams();
  const dispatch = useDispatch();
  const navigate = useNavigate();


  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [role, setRole] = useState('');
  const [selectedPermissions, setSelectedPermissions] = useState([]);
  const [availablePermissions, setAvailablePermissions] = useState([]);
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [error, setError] = useState('');

  // Modal configuration state for both success and error
  const [modalConfig, setModalConfig] = useState({
    show: false,
    type: '', // "error" or "success"
    message: '',
  });

  // Function to hide the modal
  const handleModalClose = () => {
    setModalConfig((prev) => ({ ...prev, show: false }));
    if (modalConfig.type === 'success') {
      navigate('/users');
    }
  };

  // Helpers to set modal configuration
  const handleApiFailure = (errorMessage) => {
    setModalConfig({ show: true, type: 'error', message: errorMessage });
  };

  const handleApiSuccess = (successMessage) => {
    setModalConfig({ show: true, type: 'success', message: successMessage });
  };

  useEffect(() => {
    if (userId) {
      dispatch(fetchUserById(userId));
    }
  }, [dispatch, userId]);

  // Fetch available permissions from the API.
  useEffect(() => {
    dispatch(fetchPermissions())
      .then((data) => {
        setAvailablePermissions(data);
      })
      .catch((error) => {
        console.error('Error fetching permissions:', error);
      });
  }, [dispatch]);

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (password !== confirmPassword) {
      setError('Passwords do not match. Please try again.');
      return;
    }
    setError('');

    const userData = { email, name, password, role, permissions: selectedPermissions };
    const result = await dispatch(createUser(userData));
    
    if (result && result.error && result.error.response) {
      handleApiFailure(result.error.response.data.error);
      return;
    }
    
    await dispatch(fetchUsers({ search: '', page: 1 }));
    // Show success modal
    handleApiSuccess('User created successfully.');
    // Optionally, navigate after a delay:
    setTimeout(() => navigate('/users'), 7000);
  };

  return (
    <Container className="mt-4">
      <Card>
        <Card.Body>
          <Card.Title>Create a New User</Card.Title>
          <Accordion className="mb-2">
            <Accordion.Item eventKey="0">
              <Accordion.Header>Help</Accordion.Header>
              <Accordion.Body>
                <h6>User Information</h6>
                <p>- Please enter full name or company name in the Name field.</p>
                <p>- Enter a valid email address.</p>
                <p>- Select the appropriate role desired:</p>
                <ul>
                  <li>Admin - Full access to all features.</li>
                  <li>User - Can only access apps, no admin features.</li>
                </ul>
                <p>- Select permissions for the user:</p>
                <ul>
                  <li>Shipping - Access to shipping app.</li>
                  <li>User Creation - Can create new users.</li>
                  <li>Logs - Can view logs.</li>
                </ul>
              </Accordion.Body>
            </Accordion.Item>
          </Accordion>
          <Form onSubmit={handleSubmit}>
            <Form.Group controlId="formName" className="mb-2">
              <Form.Label>Name</Form.Label>
              <Form.Control
                type="text"
                placeholder="Enter name"
                value={name}
                onChange={(e) => setName(e.target.value)}
                required
              />
            </Form.Group>
            <Form.Group controlId="formEmail" className="mb-2">
              <Form.Label>Email</Form.Label>
              <Form.Control
                type="email"
                placeholder="Enter email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
              />
            </Form.Group>
            <Form.Group controlId="password" className="mb-2">
              <Form.Label>New Password</Form.Label>
              <Form.Control
                type="password"
                placeholder="Enter new password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
              />
            </Form.Group>
            <Form.Group controlId="confirmPassword" className="mb-2">
              <Form.Label>Confirm New Password</Form.Label>
              <Form.Control
                type="password"
                placeholder="Re-enter new password"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                required
              />
            </Form.Group>
            <CreateUserRoleSelect role={role} setRole={setRole} />
            <PermissionsForm 
                availablePermissions={availablePermissions} 
                selectedPermissions={selectedPermissions}
                setSelectedPermissions={setSelectedPermissions} />
            <Button variant="primary" type="submit" className="mt-3" block>
              Create
            </Button>
          </Form>
        </Card.Body>
      </Card>

      {/* Global Modal for Success and Error */}
      <GlobalModal
        show={modalConfig.show}
        onHide={handleModalClose}
        type={modalConfig.type}
        message={modalConfig.message}
      />
    </Container>
  );
}

export default UserCreate;
